import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { ShieldCheck, Plus, Trash, CircleNotch } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";
import { NavTabs } from "@/components/NavTabs";
import { Footer } from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Oct 27 2026, user's spec: "Admin can enable or assign Quality Activity
// against individual Production Models... Do not automatically enable Quality
// Activity for all Production Models." Only the models listed here route their
// scrap through a Quality Decision Note; every other model keeps the existing
// Production Confirmation exactly as it is.
export default function QualityActivityPage() {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modelId, setModelId] = useState("");
  const [description, setDescription] = useState("");
  const [siteId, setSiteId] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/quality-activity-models`);
      setModels(data.models || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not read the Quality Activity list");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!modelId.trim()) return toast.error("Enter the Production Model ID");
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/quality-activity-models`, {
        production_model_id: modelId.trim(), description: description.trim(), site_id: siteId.trim(),
      });
      setModels(data.models || []);
      toast.success(`Quality Activity assigned to ${modelId.trim().toUpperCase()}`, {
        description: "Scrap reported on this model now goes to quality before it can be posted.",
      });
      setModelId("");
      setDescription("");
      setSiteId("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not assign Quality Activity");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    setConfirmRemove(null);
    try {
      const { data } = await axios.delete(`${API}/quality-activity-models/${encodeURIComponent(id)}`);
      setModels(data.models || []);
      toast.success(`${id} is back on the normal confirmation flow`, {
        description: `${data.notes_cancelled || 0} open note(s) closed - its lots are free again.`,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not remove it");
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex flex-col">
      <header className="min-h-16 bg-[#0E7C86] shadow-[0_1px_3px_0_rgba(16,24,40,0.15)] flex items-center justify-between px-3 sm:px-5 shrink-0 z-10 gap-2 sm:gap-4">
        <div className="flex items-center gap-3 shrink-0" data-testid="app-title">
          <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
            <ShieldCheck size={18} weight="fill" className="text-white" />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="font-heading text-[16px] font-bold text-white tracking-tight">Production Control</span>
            <span className="font-sans text-[12px] text-white/70 hidden sm:inline">Quality Activity</span>
          </div>
        </div>
        <div className="w-px h-7 bg-white/25 shrink-0" />
        <div className="flex items-center gap-3 flex-1 justify-start min-w-0">
          <NavTabs />
        </div>
      </header>
      <main className="flex-1 max-w-[1100px] w-full mx-auto px-4 py-6 space-y-5" data-testid="quality-activity-page">
        <div className="flex items-center gap-2">
          <ShieldCheck size={20} className="text-[#0E7C86]" weight="fill" />
          <h1 className="font-heading text-lg font-bold text-[#101828]">Quality Activity</h1>
        </div>
        <p className="text-sm text-[#475467] max-w-3xl">
          Only the production models listed here follow the Quality Decision route: when an operator reports scrap on one
          of them, nothing posts to SAP - a Quality Decision Note is raised and the lot is blocked until quality accepts
          the scrap. Every model NOT on this list keeps the existing Production Confirmation and scrap posting, unchanged.
        </p>

        <div className="bg-white border border-[#D0D5DD] rounded-sm p-4 space-y-3">
          <h2 className="text-sm font-bold text-[#101828]">Assign Quality Activity</h2>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <div className="space-y-1">
              <Label className="text-xs font-bold text-[#344054]">Production Model ID *</Label>
              <Input
                value={modelId}
                onChange={(e) => setModelId(e.target.value.toUpperCase())}
                placeholder="e.g. PM-BK-0050"
                className="h-9 font-mono"
                data-testid="quality-activity-model-input"
              />
            </div>
            <div className="space-y-1 sm:col-span-2">
              <Label className="text-xs font-bold text-[#344054]">Description</Label>
              <Input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What this model makes"
                className="h-9"
                data-testid="quality-activity-description-input"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-bold text-[#344054]">Site</Label>
              <Input
                value={siteId}
                onChange={(e) => setSiteId(e.target.value.toUpperCase())}
                placeholder="P1"
                className="h-9"
                data-testid="quality-activity-site-input"
              />
            </div>
          </div>
          <Button onClick={add} disabled={saving} data-testid="quality-activity-add-button">
            <Plus size={14} weight="bold" className="mr-1.5" />
            {saving ? "Assigning..." : "Assign Quality Activity"}
          </Button>
          <p className="text-[11px] text-[#667085]">
            The Production Model ID is the one shown on the lot rows of the Production Confirmation screen.
          </p>
        </div>

        <div className="bg-white border border-[#D0D5DD] rounded-sm">
          <div className="px-4 py-3 border-b border-[#EAECF0] flex items-center gap-2">
            <h2 className="text-sm font-bold text-[#101828]">Models With Quality Activity</h2>
            <span className="text-xs text-[#667085]">{models.length} model(s)</span>
          </div>
          <table className="w-full text-[13px]" data-testid="quality-activity-table">
            <thead className="bg-[#F9FAFB]">
              <tr>
                {["Production Model", "Description", "Site", "Assigned", ""].map((h) => (
                  <th key={h} className="px-3 py-2 text-left font-bold text-[#475467] border-b border-[#EAECF0]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={5} className="px-3 py-8 text-center text-[#98A2B3]">
                  <CircleNotch size={18} className="animate-spin inline" /></td></tr>
              )}
              {!loading && models.length === 0 && (
                <tr><td colSpan={5} className="px-3 py-8 text-center text-[#98A2B3]" data-testid="quality-activity-empty">
                  No model has Quality Activity yet - every model is on the normal confirmation flow.
                </td></tr>
              )}
              {models.map((m) => (
                <tr key={m.production_model_id} className="border-b border-[#F2F4F7] last:border-b-0" data-testid={`quality-activity-row-${m.production_model_id}`}>
                  <td className="px-3 py-2 font-mono font-bold text-[#175CD3]">{m.production_model_id}</td>
                  <td className="px-3 py-2 text-[#475467]">{m.description || "—"}</td>
                  <td className="px-3 py-2">{m.site_id || "—"}</td>
                  <td className="px-3 py-2 text-[#667085] text-[12px]">
                    {m.assigned_by || "—"}
                    {m.assigned_at ? ` · ${new Date(m.assigned_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}` : ""}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {confirmRemove === m.production_model_id ? (
                      <span className="inline-flex items-center gap-2">
                        <span className="text-[12px] text-[#B42318]">Scrap goes straight to SAP again. Sure?</span>
                        <Button
                          size="sm" className="h-7 bg-[#B42318] hover:bg-[#912018]"
                          onClick={() => remove(m.production_model_id)}
                          data-testid={`quality-activity-remove-confirm-${m.production_model_id}`}
                        >
                          Yes, remove
                        </Button>
                        <Button variant="outline" size="sm" className="h-7 bg-white" onClick={() => setConfirmRemove(null)}
                          data-testid={`quality-activity-remove-cancel-${m.production_model_id}`}>
                          Keep
                        </Button>
                      </span>
                    ) : (
                      <Button
                        variant="outline" size="sm" className="h-7 bg-white text-[#B42318] border-[#FECDCA]"
                        onClick={() => setConfirmRemove(m.production_model_id)}
                        data-testid={`quality-activity-remove-${m.production_model_id}`}
                      >
                        <Trash size={13} weight="bold" className="mr-1" /> Remove
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
      <Footer />
    </div>
  );
}
